/**
 * CategoryDiscovery - Compact inline category pills
 * 
 * Redesign 2026: From 72x80px tiles to compact inline pills
 * Saves ~50px vertical space on mobile
 * 
 * Layout:
 * - Mobile: Inline pills (h-[30px]) in scrollable row, shares row with DeliveryModeToggle
 * - Desktop: Slightly larger pills (h-9), single row
 */
import { memo, useCallback, useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFilterStore } from '@/lib/FilterStore';
import { useShallow } from 'zustand/react/shallow';

interface Category {
  id: string;
  label: string;
  emoji: string;
  cuisineId: number;
}

interface CategoryDiscoveryProps {
  variant?: 'desktop' | 'mobile';
  onCategorySelect?: (category: Category) => void;
  className?: string;
}

const POPULAR_DISHES: Category[] = [
  { id: 'pizza', label: 'Pizza', emoji: '🍕', cuisineId: 1 },
  { id: 'burger', label: 'Burger', emoji: '🍔', cuisineId: 2 },
  { id: 'doener', label: 'Döner', emoji: '🥙', cuisineId: 3 },
  { id: 'sushi', label: 'Sushi', emoji: '🍣', cuisineId: 8 },
  { id: 'currywurst', label: 'Currywurst', emoji: '🌭', cuisineId: 13 },
];

const CUISINE_REGIONS: Category[] = [
  { id: 'italienisch', label: 'Italienisch', emoji: '🇮🇹', cuisineId: 4 },
  { id: 'asiatisch', label: 'Asiatisch', emoji: '🥢', cuisineId: 7 },
  { id: 'indisch', label: 'Indisch', emoji: '🍛', cuisineId: 6 },
  { id: 'griechisch', label: 'Griechisch', emoji: '🇬🇷', cuisineId: 14 },
  { id: 'amerikanisch', label: 'American', emoji: '🇺🇸', cuisineId: 15 },
];

const OCCASIONS: Category[] = [
  { id: 'fruehstueck', label: 'Frühstück', emoji: '🥐', cuisineId: 9 },
  { id: 'healthy', label: 'Gesund', emoji: '🥗', cuisineId: 10 },
];

const CUISINE_CATEGORIES: Category[] = [
  ...POPULAR_DISHES,
  ...CUISINE_REGIONS,
  ...OCCASIONS,
];

interface CategoryPillProps {
  category: Category;
  isActive: boolean;
  onClick: () => void;
  variant: 'desktop' | 'mobile';
}

const CategoryPill = memo(function CategoryPill({ 
  category, isActive, onClick, variant 
}: CategoryPillProps) {
  const isDesktop = variant === 'desktop';
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex-shrink-0 inline-flex items-center gap-1.5 transition-colors duration-150",
        "touch-manipulation select-none cursor-pointer whitespace-nowrap",
        "rounded-full border",
        isDesktop 
          ? "h-9 px-3.5 text-sm font-medium" 
          : "h-[30px] px-2.5 text-xs font-medium",
        isActive 
          ? "bg-[#009544] border-[#009544] text-white" 
          : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300",
        !isDesktop && "active:scale-[0.97]"
      )}
      aria-pressed={isActive}
    >
      <span className={isDesktop ? "text-base leading-none" : "text-sm leading-none"}>
        {category.emoji}
      </span>
      <span>{category.label}</span>
    </button>
  );
});

export const CategoryDiscovery = memo(function CategoryDiscovery({
  variant = 'mobile',
  onCategorySelect,
  className
}: CategoryDiscoveryProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const { cuisineIds, setCuisines } = useFilterStore(useShallow(state => ({
    cuisineIds: state.cuisineIds,
    setCuisines: state.setCuisines
  })));
  
  const categories = CUISINE_CATEGORIES;
  
  const handleSelect = useCallback((category: Category) => {
    const isSelected = cuisineIds.includes(category.cuisineId);
    if (isSelected) {
      setCuisines(cuisineIds.filter(id => id !== category.cuisineId));
    } else {
      setCuisines([...cuisineIds, category.cuisineId]);
    }
    onCategorySelect?.(category);
  }, [cuisineIds, setCuisines, onCategorySelect]);
  
  // Scroll state for desktop arrow buttons
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || variant !== 'desktop') return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateScrollState); ro.disconnect(); };
  }, [variant, updateScrollState]);

  const scrollBy = useCallback((dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  }, []);

  if (variant === 'desktop') {
    return (
      <div className={cn("relative min-w-0 group", className)}>
        {/* Left fade + arrow */}
        <div
          className={cn(
            "absolute left-0 top-0 bottom-0 z-10 flex items-center",
            "pointer-events-none transition-opacity duration-200",
            canScrollLeft ? "opacity-100" : "opacity-0"
          )}
        >
          <div className="w-10 h-full bg-gradient-to-r from-white via-white/80 to-transparent" />
          <button
            onClick={() => scrollBy(-1)}
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2",
              "w-7 h-7 flex items-center justify-center rounded-full",
              "bg-white border border-gray-200 shadow-md",
              "hover:bg-gray-50 hover:shadow-lg active:scale-95",
              "transition-all duration-150",
              canScrollLeft ? "pointer-events-auto" : "pointer-events-none"
            )}
            aria-label="Nach links scrollen"
            tabIndex={canScrollLeft ? 0 : -1}
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <div
          ref={scrollRef}
          className="flex items-center gap-1.5 flex-nowrap overflow-x-auto scrollbar-hide px-8"
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          {categories.map((category) => (
            <CategoryPill
              key={category.id}
              category={category}
              isActive={cuisineIds.includes(category.cuisineId)}
              onClick={() => handleSelect(category)}
              variant="desktop"
            />
          ))}
          <div className="w-2 flex-shrink-0 h-1" aria-hidden="true" />
        </div>
        {/* Right fade + arrow */}
        <div
          className={cn(
            "absolute right-0 top-0 bottom-0 z-10 flex items-center",
            "pointer-events-none transition-opacity duration-200",
            canScrollRight ? "opacity-100" : "opacity-0"
          )}
        >
          <div className="w-10 h-full bg-gradient-to-l from-white via-white/80 to-transparent" />
          <button
            onClick={() => scrollBy(1)}
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2",
              "w-7 h-7 flex items-center justify-center rounded-full",
              "bg-white border border-gray-200 shadow-md",
              "hover:bg-gray-50 hover:shadow-lg active:scale-95",
              "transition-all duration-150",
              canScrollRight ? "pointer-events-auto" : "pointer-events-none"
            )}
            aria-label="Nach rechts scrollen"
            tabIndex={canScrollRight ? 0 : -1}
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>
    );
  }
  
  // Mobile: Inline pills in scrollable row
  return (
    <div className={cn("flex items-center", className)}>
      <div
        ref={scrollRef}
        className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide"
        style={{ 
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {categories.map((category) => (
          <CategoryPill
            key={category.id}
            category={category}
            isActive={cuisineIds.includes(category.cuisineId)}
            onClick={() => handleSelect(category)}
            variant="mobile"
          />
        ))}
        <div className="w-2 flex-shrink-0 h-1" aria-hidden="true" />
      </div>
    </div>
  );
});

export { CUISINE_CATEGORIES };
export type { Category, CategoryDiscoveryProps };
